import React from "react";
import { useEffect } from "react";

const TermsOfService = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  
  return (
    <section className="bg-gray-900 text-white py-16 px-6">
      <div className="max-w-5xl mx-auto">
        <h1 className="text-4xl font-bold mb-6 text-purple-400">Trvllr™: Terms of Service</h1>
        <p className="text-gray-400 mb-6">
          Last Updated: <span className="font-semibold text-white">[03.11.2025]</span>
        </p>

        {/* 1. Introduction */}
        <div className="mb-8">
          <h2 className="text-2xl font-semibold text-purple-300 mb-3">1. Introduction</h2>
          <p className="text-gray-300">
            Welcome to **Trvllr™**, a platform designed for **travel healthcare professionals, recruiters, and agencies** to engage in job placements, contract negotiations, and industry insights. By accessing or using Trvllr™, you agree to comply with these **Terms of Service**. If you do not agree, please do not use our services.
          </p>
        </div>

        {/* 2. Eligibility & User Roles */}
        <div className="mb-8">
          <h2 className="text-2xl font-semibold text-purple-300 mb-3">2. Eligibility & User Roles</h2>
          <p className="text-gray-300">
            To use Trvllr™, you must be at least **18 years old** and legally able to enter into contracts. Users fall into the following categories:
          </p>
          <ul className="list-disc list-inside text-gray-300 space-y-2">
            <li><strong>Professionals:</strong> Travel healthcare workers, gig professionals, independent contractors.</li>
            <li><strong>Recruiters & Employers:</strong> Agencies, hospitals, staffing firms, hiring managers.</li>
            <li><strong>Platform Admins:</strong> Internal Trvllr™ team overseeing compliance and operations.</li>
          </ul>
        </div>

        {/* 3. Platform Usage & Features */}
        <div className="mb-8">
          <h2 className="text-2xl font-semibold text-purple-300 mb-3">3. Platform Usage & Features</h2>
          <p className="text-gray-300">
            Trvllr™ provides **job matching, recruiter bidding, contract analysis, and professional insights** through AI-driven tools. By using Trvllr™, you agree to:
          </p>
          <ul className="list-disc list-inside text-gray-300 space-y-2">
            <li>Use **ISO Auction™**, **AutoJM™**, and **AutoNeg™** responsibly for fair contract negotiations.</li>
            <li>Engage in **credential verification and compliance tracking** through Trvllr™.</li>
            <li>Use **Shift Manager™** for scheduling and **TrIPS™** for travel and work-life balance planning.</li>
            <li>Allow Trvllr™ to analyze non-personal data for market trends via **TrInsyte™**.</li>
          </ul>
        </div>

        {/* 4. Account Registration & Security */}
        <div className="mb-8">
          <h2 className="text-2xl font-semibold text-purple-300 mb-3">4. Account Registration & Security</h2>
          <p className="text-gray-300">
            You are responsible for safeguarding your account credentials. Trvllr™ reserves the right to suspend or terminate accounts that violate these terms, including **fraudulent job postings, spamming, or misleading contract negotiations**.
          </p>
        </div>

        {/* 5. Payment & Subscription Services */}
        <div className="mb-8">
          <h2 className="text-2xl font-semibold text-purple-300 mb-3">5. Payment & Subscription Services</h2>
          <p className="text-gray-300">
            Recruiters and employers may subscribe to **premium features**, including **ISO Auctions™, targeted job postings, TrInsyte™ analytics, and contract optimization tools**. Payment terms are as follows:
          </p>
          <ul className="list-disc list-inside text-gray-300 space-y-2">
            <li>Subscription fees are **non-refundable**, except as required by law.</li>
            <li>Trvllr™ reserves the right to **change pricing** with notice.</li>
            <li>Failure to make payments may result in **suspension of premium features**.</li>
          </ul>
        </div>

        {/* 6. Professional & Recruiter Responsibilities */}
        <div className="mb-8">
          <h2 className="text-2xl font-semibold text-purple-300 mb-3">6. Professional & Recruiter Responsibilities</h2>
          <p className="text-gray-300">
            All users must adhere to ethical hiring and professional engagement:
          </p>
          <ul className="list-disc list-inside text-gray-300 space-y-2">
            <li>Professionals must provide **accurate credentials, certifications, and employment history**.</li>
            <li>Recruiters must disclose **job details, pay transparency, and contract terms** clearly.</li>
            <li>Users must **not engage in fraudulent offers, job misrepresentation, or contract violations**.</li>
          </ul>
        </div>

        {/* 7. Data Privacy & Security */}
        <div className="mb-8">
          <h2 className="text-2xl font-semibold text-purple-300 mb-3">7. Data Privacy & Security</h2>
          <p className="text-gray-300">
            Trvllr™ values your privacy and protects data under **HIPAA, GDPR, and CCPA** compliance. Our <a href="/privacy-policy" className="text-purple-400 hover:text-purple-300">Privacy Policy</a> outlines how we handle your data.
          </p>
        </div>

        {/* 8. Dispute Resolution */}
        <div className="mb-8">
          <h2 className="text-2xl font-semibold text-purple-300 mb-3">8. Dispute Resolution</h2>
          <p className="text-gray-300">
            Any disputes must be resolved through **binding arbitration** in accordance with industry standards. Trvllr™ is not responsible for contractual disputes between professionals and recruiters.
          </p>
        </div>

        {/* 9. Limitation of Liability */}
        <div className="mb-8">
          <h2 className="text-2xl font-semibold text-purple-300 mb-3">9. Limitation of Liability</h2>
          <p className="text-gray-300">
            Trvllr™ is a **facilitator** of professional-recruiter interactions and **is not liable** for issues arising from job placements, contract negotiations, or disputes.
          </p>
        </div>

        {/* 10. Changes to Terms */}
        <div className="mb-8">
          <h2 className="text-2xl font-semibold text-purple-300 mb-3">10. Changes to These Terms</h2>
          <p className="text-gray-300">
            Trvllr™ may update these Terms of Service periodically. Users will be notified of major changes via email or platform announcements.
          </p>
        </div>

        {/* Contact */}
        <div className="mb-8">
          <h2 className="text-2xl font-semibold text-purple-300 mb-3">11. Contact Us</h2>
          <p className="text-gray-300">
            Questions about these Terms? Reach out to us at:
          </p>
          <ul className="text-gray-400 mt-4 space-y-2">
            <li>📧 <a href="mailto:legal@trvllrpro.io" className="text-purple-400 hover:text-purple-300">legal@trvllrpro.io</a></li>
          </ul>
        </div>

      </div>
    </section>
  );
};

export default TermsOfService;
